/**
 * The app frame's height, measured rather than declared.
 *
 * `.screen` was `height: 100svh`. An iOS Home Screen app reports the CSS
 * viewport units short: on the owner's iPhone the frame stopped about 150pt
 * above the screen edge, leaving a band under the Now-Bar on every screen
 * (and an unstyled Plaid Link, placed after the frame, showed through it).
 * Reports from other installed web apps (iOS 18, 26 and 27) have `100svh`,
 * `100dvh` and `100%` short, and `100lvh` too tall on some releases, while
 * `window.innerHeight` matched the area actually painted in each one measured.
 *
 * So the frame takes its height from `--app-h`, set here from innerHeight.
 * The CSS keeps `100svh` as the fallback for the moment before this runs.
 * In the browser suite's engines the two agree, which is why only the iPhone
 * ever showed the band.
 *
 * Writes a custom property through the CSSOM, which CSP does not govern.
 */
export function trackWindowHeight(root: HTMLElement = document.documentElement): void {
  const measure = () => root.style.setProperty('--app-h', `${window.innerHeight}px`);
  measure();
  window.addEventListener('resize', measure);
  // Returning from the background, and the keyboard closing, are the two
  // moments iOS has been seen to leave the viewport at a stale size.
  window.addEventListener('pageshow', measure);
  window.visualViewport?.addEventListener('resize', measure);
}
